import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const URG = [
    { v: "low", l: "Low" },
    { v: "normal", l: "Normal" },
    { v: "high", l: "High" },
    { v: "urgent", l: "Urgent" },
];

export default function PortalNewRequest() {
    const nav = useNavigate();
    const [services, setServices] = useState([]);
    const [form, setForm] = useState({
        service_slug: "",
        title: "",
        description: "",
        urgency: "normal",
    });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        api.get("/services").then((r) => {
            setServices(r.data);
            if (r.data.length) setForm((f) => ({ ...f, service_slug: r.data[0].slug }));
        });
    }, []);

    const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/case-requests", form);
            toast.success("Request submitted. A consultant will reach out within 1 business day.");
            nav("/portal/cases");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to submit");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-3xl" data-testid="portal-new-request-page">
            <div className="label-tag">New request</div>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-bold">Open a new case.</h1>
            <p className="mt-3 text-white/60">
                Provide enough detail for us to assign the right consultant. You can attach
                documents after we've confirmed.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-5 bg-ink-900 border border-white/10 rounded-2xl p-8" data-testid="portal-request-form">
                <Field label="Service" required>
                    <select required data-testid="req-service" value={form.service_slug} onChange={upd("service_slug")} className="pt-input">
                        {services.map((s) => (
                            <option key={s.slug} value={s.slug} className="bg-ink-900">{s.title}</option>
                        ))}
                    </select>
                </Field>
                <Field label="Title" required>
                    <input required data-testid="req-title" value={form.title} onChange={upd("title")} className="pt-input" placeholder="e.g., Step 2 grievance — unjust termination" />
                </Field>
                <Field label="Urgency">
                    <div className="flex flex-wrap gap-2">
                        {URG.map((u) => (
                            <button
                                key={u.v}
                                type="button"
                                data-testid={`req-urgency-${u.v}`}
                                onClick={() => setForm((f) => ({ ...f, urgency: u.v }))}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-semibold border transition-all ${
                                    form.urgency === u.v
                                        ? "bg-gold-500 text-ink-900 border-gold-500"
                                        : "bg-ink-900 text-white/70 border-white/15 hover:border-gold-500/60"
                                }`}
                            >
                                {u.l}
                            </button>
                        ))}
                    </div>
                </Field>
                <Field label="Description" required>
                    <textarea required rows={6} data-testid="req-description" value={form.description} onChange={upd("description")} className="pt-input resize-none" placeholder="Describe the facts, dates, parties involved, and what outcome you're seeking." />
                </Field>
                <button
                    type="submit"
                    disabled={loading}
                    data-testid="req-submit-btn"
                    className="w-full px-7 py-4 rounded-full bg-gold-500 text-ink-900 font-bold uppercase tracking-wider text-sm hover:bg-gold-400 disabled:opacity-60"
                >
                    {loading ? "Submitting…" : "Submit request"}
                </button>
            </form>
            <style>{`.pt-input { width:100%; background:#0A0A0A; border:1px solid rgba(255,255,255,0.12); border-radius:10px; padding:12px 14px; color:#fff; }`}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="block text-xs tracking-[0.2em] uppercase text-white/60 mb-2">{label}{required ? " *" : ""}</span>
            {children}
        </label>
    );
}
